import { Theme } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { useTranslate } from '@/hooks/useTranslate';

import { DocumentStatus } from '@/types/document';

import { getDataTestId } from '@/utils/browser';

import { StatusIcon } from '../../DataDisplay/StatusIcon';
import { ListActionItem } from './ListActionItem';

import { ActionButton } from './ActionList.styled';

type DocumentActionItemProps = Readonly<{
  title: string;
  description?: string;
  subDescription?: string;
  richDescription?: ReactNode;
  status?: DocumentStatus;
  onClick?: () => void;
  enabledForAllStatuses?: boolean;
  disabled?: boolean;
  disabledButton?: boolean;
  hideAction?: boolean;
}>;

export function DocumentActionItem({
  title,
  description,
  subDescription,
  richDescription,
  status,
  onClick,
  disabled,
  disabledButton,
  hideAction,
  enabledForAllStatuses,
}: DocumentActionItemProps) {
  const { t } = useTranslate();
  const { text, color, actionButton } = useMemo(() => {
    let content = description;
    let iconButton: ReactNode;
    let handleClick = undefined;

    if (!disabled) {
      if (
        enabledForAllStatuses ||
        (status && [DocumentStatus.Rejected].includes(status))
      ) {
        handleClick = onClick;
      }
    }

    switch (status) {
      case DocumentStatus.InReview:
        content = t('status.underReview');
        iconButton = (
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={handleClick}
            disabled={!!disabledButton}
          >
            <StatusIcon value={status} />
          </ActionButton>
        );
        break;
      case DocumentStatus.Rejected:
        content = t('status.rejected');
        iconButton = (
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={handleClick}
            disabled={!!disabledButton}
          >
            <StatusIcon value={status} />
          </ActionButton>
        );
        break;

      case DocumentStatus.DefaultApproved:
        content = t('status.approved');
        iconButton = (
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={handleClick}
            disabled={!!disabledButton}
          >
            <StatusIcon value={DocumentStatus.Approved} />
          </ActionButton>
        );
        break;
      case DocumentStatus.Approved:
        content = t('status.approved');
        iconButton = (
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={handleClick}
            disabled={!!disabledButton}
          >
            <StatusIcon value={status} />
          </ActionButton>
        );
        break;
      case DocumentStatus.Completed:
        content = t('status.completed');
        iconButton = (
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={handleClick}
            disabled={!!disabledButton}
          >
            <StatusIcon value={DocumentStatus.Approved} />
          </ActionButton>
        );
        break;
    }

    return {
      text:
        subDescription && content
          ? `${content} - ${subDescription}`
          : (content ?? subDescription),
      color: (theme: Theme) =>
        theme.palette.status[String(status)]?.main ?? theme.palette.grey[800],
      actionButton: iconButton,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    description,
    disabled,
    enabledForAllStatuses,
    onClick,
    status,
    subDescription,
  ]);

  return (
    <ListActionItem
      title={title}
      description={text}
      richDescription={richDescription}
      descriptionColor={color}
      actionButton={hideAction ? null : actionButton}
      onClick={onClick}
      disabled={disabled}
    />
  );
}
